"use client";

import { useState, useEffect } from "react";
import { X, Copy, Check, ExternalLink } from "lucide-react";

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void; // Called when X button or Escape is pressed - only closes modal (no navigation per FR-6.1)
  onViewPost?: () => void; // Called when "View Your Post" button is clicked - navigates to post and closes modal
  postUrl: string;
  pseudonym: string;
  waveformUrl?: string | null;
  ogImageDataUrl?: string | null; // Client-generated data URL for immediate display
}

/**
 * Modal that appears after successfully publishing a post
 * Celebrates the publish and encourages sharing to X
 */
const PLACEHOLDER_BARS = [
  28, 46, 38, 64, 52, 80, 56, 42, 68, 50, 34, 62, 44, 72, 58, 36,
  60, 48, 30, 66, 40, 74, 54, 32, 70, 46, 28, 64, 52, 78, 56, 34,
];

export function ShareModal({
  isOpen,
  onClose,
  onViewPost,
  postUrl,
  pseudonym,
  waveformUrl,
  ogImageDataUrl,
}: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Construct OG image URL from postUrl if waveformUrl is not a valid full URL
  const getOgImageUrl = (): string | null => {
    // Get the base URL from window.location or postUrl
    const getBaseUrl = (): string => {
      // Try to get from postUrl if it's absolute
      if (postUrl && postUrl.startsWith('http')) {
        try {
          const url = new URL(postUrl);
          return url.origin;
        } catch {
          // Fall through to window.location
        }
      }
      // Fall back to window.location.origin
      if (typeof window !== 'undefined') {
        return window.location.origin;
      }
      return '';
    };
    
    const baseUrl = getBaseUrl();
    
    // If waveformUrl is a full URL (not localhost), use it
    if (waveformUrl && typeof waveformUrl === 'string' && waveformUrl.startsWith('http') && !waveformUrl.includes('localhost')) {
      return waveformUrl;
    }
    
    // If waveformUrl is a relative path, make it absolute
    if (waveformUrl && typeof waveformUrl === 'string' && waveformUrl.startsWith('/')) {
      return `${baseUrl}${waveformUrl}`;
    }
    
    // Otherwise, construct from postUrl by extracting the slug
    const slugMatch = postUrl?.match(/\/s\/([^/?]+)/);
    if (slugMatch && slugMatch[1] && baseUrl) {
      return `${baseUrl}/og/voice-note/${slugMatch[1]}`;
    }
    
    return null;
  };

  const ogImageUrl = ogImageDataUrl || getOgImageUrl(); // Prefer client-generated data URL for immediate display

  // Reset image state when modal opens or URL changes
  // Data URLs load instantly, so mark as loaded immediately
  useEffect(() => {
    if (isOpen && ogImageUrl) {
      // Data URLs load instantly, so mark as loaded immediately
      if (ogImageUrl.startsWith('data:')) {
        setImageLoaded(true);
        setImageError(false);
      } else {
        // CDN URLs need to load, so reset state
        setImageLoaded(false);
        setImageError(false);
      }
    }
  }, [isOpen, ogImageUrl]);

  // Early return after all hooks
  if (!isOpen) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(postUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      alert("Failed to copy link");
    }
  };

  const handleShareToX = () => {
    const text = `🎙️ Voice post on Vocly — tap to listen\n${postUrl}`;
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(tweetUrl, "_blank");
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
      aria-describedby="share-modal-description"
      onClick={(e) => {
        // Close modal when clicking the backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="relative w-full max-w-md max-h-[calc(100vh-2rem)] rounded-2xl border border-[rgba(255,255,255,0.04)] bg-gradient-to-br from-[#171727] via-[#111118] to-[#171727] p-6 shadow-[0_24px_60px_rgba(0,0,0,0.75)] overflow-y-auto my-auto"
        onClick={(e) => {
          // Prevent backdrop click from closing when clicking inside modal
          e.stopPropagation();
        }}
      >
        {/* Close button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="absolute right-4 top-4 z-[100] rounded-lg p-2 text-white/60 hover:text-white bg-black/20 hover:bg-black/40 transition-all pointer-events-auto"
          aria-label="Close"
          type="button"
          style={{ zIndex: 100 }}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Success message */}
        <div className="mb-6 text-center">
          <div className="mb-1.5 text-5xl">🎉</div>
          <h2 id="share-modal-title" className="mb-2 text-2xl font-bold text-white">
            Your post is live!
          </h2>
          <p id="share-modal-description" className="sr-only">
            Your voice post has been published. Share it on social media or copy the link.
          </p>
        </div>

        {/* Waveform preview (image or placeholder) */}
        <div className="mb-4 rounded-[14px] border border-white/10 bg-black">
          <div
            className="relative w-full overflow-hidden rounded-[10px]"
            style={{ aspectRatio: "1200 / 630" }}
          >
            {ogImageUrl ? (
              <>
                {!imageLoaded && !imageError && (
                  <div className="waveform-placeholder flex h-full w-full flex-col rounded-[10px] bg-gradient-to-br from-[#171727] via-[#111118] to-[#171727] px-4 pb-4 absolute inset-0">
                    <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-[0.18em] text-white/40">
                      <span>Vocly Preview</span>
                      <span>vocly.fm</span>
                    </div>
                    <div className="flex h-16 items-end gap-[3px]">
                      {PLACEHOLDER_BARS.map((height, index) => (
                        <div
                          key={index}
                          className="flex-1 rounded-sm bg-gradient-to-t from-[#ff3ea5] to-[#ff6ac2] opacity-60"
                          style={{ height: `${height}%` }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <img
                  src={ogImageUrl}
                  onLoad={() => {
                    setImageLoaded(true);
                  }}
                  onError={() => {
                    setImageError(true);
                  }}
                  alt="Waveform preview"
                  className={`w-full h-full object-contain ${imageLoaded ? '' : 'hidden'}`}
                  style={{ display: imageLoaded ? 'block' : 'none', objectFit: 'contain' }}
                />
              </>
            ) : (
              <div className="waveform-placeholder flex h-full w-full flex-col rounded-[10px] bg-gradient-to-br from-[#171727] via-[#111118] to-[#171727] px-4 pb-4">
                <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-[0.18em] text-white/40">
                  <span>Vocly Preview</span>
                  <span>vocly.fm</span>
                </div>
                <div className="flex h-16 items-end gap-[3px]">
                  {PLACEHOLDER_BARS.map((height, index) => (
                    <div
                      key={index}
                      className="w-1 rounded-full bg-gradient-to-t from-[#ff3ea5] via-[#ff6ac2] to-[#ffe4f5]/80"
                      style={{ height: `${height}px`, opacity: 0.4 + (index % 5) * 0.1 }}
                    />
                  ))}
                </div>
                <div className="mt-3">
                  <p className="text-lg font-semibold text-white">{pseudonym}</p>
                  <p className="text-sm text-white/50">Voice post · tap to listen</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="space-y-4">
          {/* Primary Button - Share on X */}
          <button
            onClick={handleShareToX}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#ff3ea5] to-[#ff6ac2] px-6 py-3 font-semibold text-[#160513] shadow-[0_12px_36px_rgba(255,62,165,0.6)] transition hover:brightness-[1.06] hover:translate-y-[-1px] hover:shadow-[0_16px_44px_rgba(255,62,165,0.8)]"
          >
            Share on 𝕏
            <ExternalLink className="h-4 w-4" />
          </button>

          {/* Secondary Actions - Copy Link and View Your Post side-by-side */}
          <div className="flex gap-3">
            <button
              onClick={handleCopyLink}
              className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#262636] bg-[#0a0a12] px-6 py-2.5 text-sm font-medium text-[#a3a3c2] transition hover:bg-[#171726]"
              title="Copy link"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-green-400" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  <span>Copy Link</span>
                </>
              )}
            </button>

            <button
              onClick={onViewPost || onClose}
              className="flex-1 rounded-full border border-[#262636] bg-[#0a0a12] px-6 py-2.5 text-sm font-medium text-[#a3a3c2] transition hover:bg-[#171726]"
            >
              View Your Post
            </button>
          </div>
        </div>

        {/* Pseudonym - moved to bottom */}
        <div className="mt-4 flex items-center justify-center gap-1 text-xs text-slate-400 opacity-65">
          <span>Posted as </span>
          <span className="font-medium text-slate-100">{pseudonym}</span>
        </div>
      </div>
    </div>
  );
}

